import React, { useState, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { useFamilyStore, useDomainStore } from '@/hooks/useStores';
import { DEFAULT_PET_CATEGORY } from '@/types/pet-category';
import type { LivingStatusType, PetResponse } from 'we-tree-api-client/v2';
import { PetOwnerRoleType } from 'we-tree-api-client/v2';
import { DEFAULT_PET_SEX } from '@/types/pet-sex';
import { parseDateOnly } from '@/utils/dateFormatting';
import type { DatePrecision } from '@/utils/dateFormatting';
import AddPetCreateNewFields, { type AddPetCreateFormState } from './AddPetCreateNewFields';
import AddPetLinkExistingFields from './AddPetLinkExistingFields';
import type { PetOwnerRoleValue } from '@/types/pet-owner';
import type { AddFlowLocationState } from '@/types/add-flow-navigation';
import { navigateCancelFromAddFlow } from '@/utils/navigateCancelFromAddFlow';
import PlanUpgradeAlert from '@/components/shared/PlanUpgradeAlert';
import { getUpgradeAwareErrorParts, isFamilyOwner } from '@/utils/familyPlan';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';

import '@/components/person/person-edit-form.css';
import '@/components/shared/add-flow-entry-toggle.css';
import './add-pet-screen.css';

function getSubmitLabel(entryMode: 'create' | 'link', isSubmitting: boolean): string {
  if (isSubmitting) return 'Saving…';
  return entryMode === 'link' ? 'Link pet' : 'Add pet';
}

const AddPetScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const { t: tForms } = useTranslation('forms');
  const { familyId: familyIdParam } = useParams<{ familyId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as AddFlowLocationState | undefined;
  const familyStore = useFamilyStore();
  const domainStore = useDomainStore();

  const parentFamilyMemberId = searchParams.get('parentId') ?? '';

  const entryMode = searchParams.get('entry') === 'link' ? 'link' : 'create';
  const setEntryMode = (mode: 'create' | 'link') => {
    const next = new URLSearchParams(searchParams);
    if (mode === 'link') next.set('entry', 'link');
    else next.delete('entry');
    setSearchParams(next, { replace: true });
  };

  const parentPerson = familyIdParam && parentFamilyMemberId ? familyStore.getPerson(parentFamilyMemberId) : null;
  const spouseIds = parentPerson?.spouseFamilyMemberIds ?? [];
  const childIds = parentPerson ? familyStore.getChildren(parentFamilyMemberId).map((c) => c.familyMemberId) : [];
  const potentialAdditionalOwners = [...spouseIds.filter((id) => id !== parentFamilyMemberId), ...childIds].filter(
    (id, idx, arr) => arr.indexOf(id) === idx
  );

  const additionalOwnerRows = potentialAdditionalOwners.map((ownerFamilyMemberId) => {
    const ownerPerson = familyStore.getPerson(ownerFamilyMemberId);
    return {
      ownerFamilyMemberId,
      displayName: ownerPerson ? `${ownerPerson.firstName} ${ownerPerson.lastName}`.trim() || 'Unknown' : 'Unknown'
    };
  });

  const [additionalOwnerChoices, setAdditionalOwnerChoices] = useState<
    Record<string, { selected: boolean; role: 'primary' | 'secondary' | 'caregiver' }>
  >({});

  const [petFormData, setPetFormData] = useState<AddPetCreateFormState>({
    displayName: '',
    category: DEFAULT_PET_CATEGORY,
    subType: '',
    sex: DEFAULT_PET_SEX,
    birthDate: '',
    birthDatePrecision: 'day' as DatePrecision,
    adoptionDate: '',
    adoptionDatePrecision: 'day' as DatePrecision,
    deathDate: '',
    deathDatePrecision: 'day' as DatePrecision,
    isDeceased: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [upgradeHint, setUpgradeHint] = useState<string | null>(null);

  const isOwner =
    Boolean(familyIdParam) &&
    domainStore.selectedFamilyId === familyIdParam &&
    isFamilyOwner(domainStore.currentFamily?.userRole);

  const [linkPetFamilyMemberId, setLinkPetFamilyMemberId] = useState('');
  const [linkPets, setLinkPets] = useState<PetResponse[]>([]);
  const [linkPetsLoading, setLinkPetsLoading] = useState(false);
  const [linkOwnerRole, setLinkOwnerRole] = useState<PetOwnerRoleValue>(PetOwnerRoleType.Secondary);

  const linkablePets = useMemo(
    () => linkPets.filter((p) => !familyStore.petResponseHasOwner(p, parentFamilyMemberId)),
    [linkPets, parentFamilyMemberId, familyStore]
  );

  const selectedLinkPet = useMemo(
    () => linkablePets.find((p) => p.familyMemberId === linkPetFamilyMemberId),
    [linkablePets, linkPetFamilyMemberId]
  );

  const linkRoleOptions = useMemo((): PetOwnerRoleValue[] => {
    if (!selectedLinkPet) {
      return [PetOwnerRoleType.Secondary, PetOwnerRoleType.Caregiver];
    }
    return familyStore.petResponseHasPrimaryOwner(selectedLinkPet)
      ? [PetOwnerRoleType.Secondary, PetOwnerRoleType.Caregiver]
      : [PetOwnerRoleType.Primary, PetOwnerRoleType.Secondary, PetOwnerRoleType.Caregiver];
  }, [selectedLinkPet, familyStore]);

  useEffect(() => {
    if (entryMode !== 'link' || !familyIdParam) return;
    let cancelled = false;
    setLinkPetsLoading(true);
    void (async () => {
      const result = await familyStore.searchPets(familyIdParam);
      if (cancelled) return;
      setLinkPetsLoading(false);
      if (result.success) {
        setLinkPets(result.data.pets ?? []);
      } else {
        setLinkPets([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entryMode, familyIdParam, familyStore]);

  useEffect(() => {
    if (!linkPetFamilyMemberId) return;
    const stillHere = linkablePets.some((p) => p.familyMemberId === linkPetFamilyMemberId);
    if (!stillHere) setLinkPetFamilyMemberId('');
  }, [linkablePets, linkPetFamilyMemberId]);

  useEffect(() => {
    const allowed = new Set(linkRoleOptions);
    if (!allowed.has(linkOwnerRole)) {
      setLinkOwnerRole(linkRoleOptions[0]);
    }
  }, [linkRoleOptions, linkOwnerRole]);

  const handleSelectLinkPet = (id: string) => {
    setLinkPetFamilyMemberId(id);
    const p = linkablePets.find((x) => x.familyMemberId === id);
    if (p) {
      const opts: PetOwnerRoleValue[] = familyStore.petResponseHasPrimaryOwner(p)
        ? [PetOwnerRoleType.Secondary, PetOwnerRoleType.Caregiver]
        : [PetOwnerRoleType.Primary, PetOwnerRoleType.Secondary, PetOwnerRoleType.Caregiver];
      setLinkOwnerRole((prev) => (opts.includes(prev) ? prev : opts[0]));
    }
  };

  const handleAdditionalOwnerToggle = (ownerFamilyMemberId: string, selected: boolean) => {
    setAdditionalOwnerChoices((prev) => ({
      ...prev,
      [ownerFamilyMemberId]: {
        ...(prev[ownerFamilyMemberId] ?? { selected: false, role: 'secondary' as const }),
        selected
      }
    }));
  };

  const handleAdditionalOwnerRoleChange = (
    ownerFamilyMemberId: string,
    role: 'primary' | 'secondary' | 'caregiver'
  ) => {
    setAdditionalOwnerChoices((prev) => ({
      ...prev,
      [ownerFamilyMemberId]: {
        ...(prev[ownerFamilyMemberId] ?? {
          selected: true,
          role: 'secondary' as const
        }),
        role
      }
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!petFormData.displayName?.trim()) {
      newErrors.displayName = t('addFlow.petNameRequired');
    }
    if (petFormData.deathDate?.trim() && !petFormData.isDeceased) {
      newErrors.deathDate = t('addFlow.markPetDeceasedForDeathDate');
    }
    if (petFormData.birthDate?.trim() && petFormData.deathDate?.trim()) {
      const pb = parseDateOnly(petFormData.birthDate);
      const pd = parseDateOnly(petFormData.deathDate);
      if (pb && pd) {
        const birthCompare = pb.year * 10000 + pb.month * 100 + pb.day;
        const deathCompare = pd.year * 10000 + pd.month * 100 + pd.day;
        if (deathCompare < birthCompare) {
          newErrors.deathDate = t('addFlow.deathAfterBirth');
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitLinkExistingPet = async () => {
    if (!familyIdParam || !parentFamilyMemberId) {
      setSubmitError(t('addFlow.missingFamilyOrOwner'));
      return;
    }
    if (!linkPetFamilyMemberId) {
      setSubmitError(t('addFlow.selectPetToLink'));
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    setUpgradeHint(null);
    try {
      const result = await familyStore.addPetOwner(linkPetFamilyMemberId, {
        ownerFamilyMemberId: parentFamilyMemberId,
        role: linkOwnerRole
      });
      if (!result.success) {
        setSubmitError(result.error?.getUserMessage?.() ?? result.error?.message ?? t('addFlow.couldNotLinkPet'));
        return;
      }
      await familyStore.loadFamilyData(familyIdParam, true);
      navigate(`/families/${familyIdParam}/pets/${linkPetFamilyMemberId}`, {
        state: locationState?.from
          ? { from: locationState.from, originalAnchorId: locationState.originalAnchorId }
          : undefined
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('addFlow.couldNotLinkPet'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setUpgradeHint(null);

    if (!familyIdParam) {
      setSubmitError(t('addFlow.missingFamily'));
      return;
    }
    if (!parentFamilyMemberId) {
      setSubmitError(t('addFlow.missingOwner'));
      return;
    }

    if (entryMode === 'link') {
      await submitLinkExistingPet();
      return;
    }

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const owners = [
        { ownerFamilyMemberId: parentFamilyMemberId, role: 'primary' as const },
        ...potentialAdditionalOwners
          .filter((id) => additionalOwnerChoices[id]?.selected)
          .map((id) => ({
            ownerFamilyMemberId: id,
            role: (additionalOwnerChoices[id]?.role ?? 'secondary') as 'primary' | 'secondary' | 'caregiver'
          }))
      ];
      const birthDateVal = petFormData.birthDate?.trim() || undefined;
      const adoptionDateVal = petFormData.adoptionDate?.trim() || undefined;
      const deathDateVal = petFormData.isDeceased ? petFormData.deathDate?.trim() || undefined : undefined;
      const petRequest = {
        familyId: familyIdParam,
        displayName: petFormData.displayName.trim(),
        category: petFormData.category,
        subType: petFormData.subType?.trim() || undefined,
        sex: petFormData.sex,
        birthDate: birthDateVal,
        birthDatePrecision: birthDateVal ? petFormData.birthDatePrecision : undefined,
        adoptionDate: adoptionDateVal,
        adoptionDatePrecision: adoptionDateVal ? petFormData.adoptionDatePrecision : undefined,
        deathDate: deathDateVal,
        deathDatePrecision: deathDateVal ? petFormData.deathDatePrecision : undefined,
        livingStatus: (petFormData.isDeceased ? 'deceased' : 'living') as LivingStatusType,
        owners
      };
      const petResult = await familyStore.createPet(petRequest);
      if (petResult.success && petResult.data) {
        await familyStore.loadFamilyData(familyIdParam, true);
        navigate(`/families/${familyIdParam}/pets/${petResult.data.familyMemberId}`, {
          state: locationState?.from
            ? { from: locationState.from, originalAnchorId: locationState.originalAnchorId }
            : undefined
        });
      } else if (!petResult.success) {
        const parts = getUpgradeAwareErrorParts(petResult.error, isOwner, t('addFlow.failedAddPet'));
        setSubmitError(parts.message);
        setUpgradeHint(parts.upgradeHint);
      }
    } catch (error) {
      setSubmitError(t('addFlow.failedAddPet'));
      // eslint-disable-next-line no-console
      console.error('Add pet error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!familyIdParam) return;
    const treeAnchorFallback =
      locationState?.originalAnchorId ?? (parentFamilyMemberId || familyStore.getRootPersonFamilyMemberId()) ?? 'first';
    navigateCancelFromAddFlow(navigate, {
      familyId: familyIdParam,
      locationState,
      treeAnchorFallback
    });
  };

  if (!familyIdParam) {
    return (
      <div className="add-pet-page add-pet-page--centered">
        <section className="add-pet-page__section add-pet-page__section--message">
          <h2 className="add-pet-page__section-title">Missing family</h2>
          <p className="add-pet-page__body">Please go back to your family and try again.</p>
          <button type="button" onClick={() => navigate('/families')} className="btn btn--primary">
            My families
          </button>
        </section>
      </div>
    );
  }

  if (!parentFamilyMemberId) {
    return (
      <div className="add-pet-page add-pet-page--centered">
        <section className="add-pet-page__section add-pet-page__section--message">
          <h2 className="add-pet-page__section-title">Missing owner</h2>
          <p className="add-pet-page__body">
            Go back to Members or Tree and use the + button on a person to add a pet.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/families/${familyIdParam}/members`)}
            className="btn btn--primary"
          >
            {tForms('actions.backToMembers')}
          </button>
        </section>
      </div>
    );
  }

  const submitLabel = getSubmitLabel(entryMode, isSubmitting);

  return (
    <div className="add-pet-page">
      <header className="add-pet-page__header">
        <h1 className="add-pet-page__title">{t('addPet.title')}</h1>
        <p className="add-pet-page__lede">
          {t('addPet.lede')} <ScreenHelpDot />
        </p>
      </header>

      <section className="add-pet-page__section">
        <form onSubmit={handleSubmit} className="add-pet-page__form person-edit-form">
          <ToggleGroup.Root
            type="single"
            value={entryMode}
            onValueChange={(v) => v && setEntryMode(v as 'create' | 'link')}
            className="add-flow-entry-toggle"
            aria-label={t('addFlow.howToAddPet')}
          >
            <ToggleGroup.Item value="create" className="add-flow-entry-toggle__item">
              {t('addFlow.createNewPet')}
            </ToggleGroup.Item>
            <ToggleGroup.Item value="link" className="add-flow-entry-toggle__item">
              {t('addFlow.linkExisting')}
            </ToggleGroup.Item>
          </ToggleGroup.Root>

          {entryMode === 'link' && (
            <AddPetLinkExistingFields
              linkPetsLoading={linkPetsLoading}
              linkablePets={linkablePets}
              linkPetFamilyMemberId={linkPetFamilyMemberId}
              onSelectPet={handleSelectLinkPet}
              linkOwnerRole={linkOwnerRole}
              onLinkOwnerRoleChange={setLinkOwnerRole}
              linkRoleOptions={linkRoleOptions}
              isSubmitting={isSubmitting}
            />
          )}

          {submitError && (
            <PlanUpgradeAlert
              message={submitError}
              upgradeHint={upgradeHint}
              onDismiss={() => {
                setSubmitError(null);
                setUpgradeHint(null);
              }}
            />
          )}

          {entryMode === 'create' && (
            <AddPetCreateNewFields
              petFormData={petFormData}
              setPetFormData={setPetFormData}
              errors={errors}
              setErrors={setErrors}
              isSubmitting={isSubmitting}
              additionalOwners={
                additionalOwnerRows.length > 0
                  ? {
                      rows: additionalOwnerRows,
                      choices: additionalOwnerChoices,
                      onToggle: handleAdditionalOwnerToggle,
                      onRoleChange: handleAdditionalOwnerRoleChange
                    }
                  : undefined
              }
            />
          )}

          <div className="person-edit-form__actions">
            <button type="button" onClick={handleCancel} disabled={isSubmitting} className="btn btn--ghost">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="btn btn--primary">
              {submitLabel}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
});

export default AddPetScreen;
